import React from "react";
import type { AgendaItem, CommentModes, CommentPosition } from "../types";
import { avatarColor, initials } from "../utils";
import { extensionOf, isImageExt } from "../attachments";
import {
  AlertIcon,
  PersonIcon,
  ShieldIcon,
  PaperclipIcon,
  LockIcon,
  CloseIcon,
  FileIcon
} from "./icons";

interface Props {
  item: AgendaItem;
  modes: CommentModes;
  text: string;
  position: CommentPosition;
  commenterName: string;
  submitting: boolean;
  showModNotice: boolean;
  characterLimit: number;
  // admin "post on behalf of the agency" toggle
  canPostAsAdmin: boolean;
  postAsAdmin: boolean;
  orgLabel: string;
  onTogglePostAsAdmin: (value: boolean) => void;
  // attachments
  allowAttachments: boolean;
  attachmentTypesAllowed: string[];
  attachments: File[];
  attachmentError?: string;
  onAddAttachments: (files: FileList) => void;
  onRemoveAttachment: (index: number) => void;
  // private (hidden) comment toggle
  allowPrivate: boolean;
  keepPrivate: boolean;
  onToggleKeepPrivate: (value: boolean) => void;
  onTextChange: (text: string) => void;
  onPositionChange: (pos: CommentPosition) => void;
  onSubmit: () => void;
}

function bytesLabel(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const ItemComposer: React.FC<Props> = ({
  item,
  modes,
  text,
  position,
  commenterName,
  submitting,
  showModNotice,
  characterLimit,
  canPostAsAdmin,
  postAsAdmin,
  orgLabel,
  onTogglePostAsAdmin,
  allowAttachments,
  attachmentTypesAllowed,
  attachments,
  attachmentError,
  onAddAttachments,
  onRemoveAttachment,
  allowPrivate,
  keepPrivate,
  onToggleKeepPrivate,
  onTextChange,
  onPositionChange,
  onSubmit
}) => {
  const anonymous = modes.identity === "anonymous";
  const name = commenterName || "your name";
  const asAgency = canPostAsAdmin && postAsAdmin;
  const accept = (attachmentTypesAllowed || []).join(",");

  return (
    <div className="pc-compose">
      <p className="pc-compose-h">
        Add your comment on {item.agenda || item.title}
      </p>
      <textarea
        className="pc-compose-input"
        placeholder="Share your comment on this item..."
        value={text}
        maxLength={characterLimit > 0 ? characterLimit : undefined}
        onChange={(e) => onTextChange(e.target.value)}
      />

      {allowAttachments && (
        <div className="pc-attach">
          {attachments.length > 0 && (
            <ul className="pc-attach-list">
              {attachments.map((f, i) => {
                const img = isImageExt(extensionOf(f.name));
                return (
                  <li key={`${f.name}-${i}`} className="pc-attach-chip">
                    {img ? (
                      <PaperclipIcon className="pc-attach-ic" />
                    ) : (
                      <FileIcon className="pc-attach-ic" />
                    )}
                    <span className="pc-attach-name">{f.name}</span>
                    <span className="pc-attach-size">{bytesLabel(f.size)}</span>
                    <button
                      type="button"
                      className="pc-attach-rm"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => onRemoveAttachment(i)}
                    >
                      <CloseIcon />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <label className="pc-attach-add">
            <PaperclipIcon className="pc-attach-ic" />
            Attach files
            <input
              type="file"
              multiple
              accept={accept || undefined}
              className="pc-attach-input"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  onAddAttachments(e.target.files);
                }
                e.target.value = "";
              }}
            />
          </label>
          {attachmentTypesAllowed.length > 0 && (
            <span className="pc-attach-hint">
              Allowed: {attachmentTypesAllowed.join(", ")}
            </span>
          )}
          {attachmentError && (
            <p className="pc-attach-err" role="alert">
              {attachmentError}
            </p>
          )}
        </div>
      )}

      {allowPrivate && (
        <label className="pc-private">
          <input
            type="checkbox"
            checked={keepPrivate}
            onChange={(e) => onToggleKeepPrivate(e.target.checked)}
          />
          <LockIcon />
          Keep my comment private - visible only to {orgLabel} staff, not to
          other residents
        </label>
      )}

      {showModNotice && (
        <div className="pc-mod-notice" role="status" aria-live="polite">
          <AlertIcon />
          <div>
            <p className="pc-mn-t">
              Comment received and recorded on the public record
            </p>
            <p className="pc-mn-d">
              Your comment was flagged by our automated civility check and is
              hidden from public view pending review. It has been received and
              added to the official record for this item.
            </p>
          </div>
        </div>
      )}

      {canPostAsAdmin && (
        <label className="pc-post-as">
          <input
            type="checkbox"
            checked={postAsAdmin}
            onChange={(e) => onTogglePostAsAdmin(e.target.checked)}
          />
          <ShieldIcon />
          Post on behalf of {orgLabel}
        </label>
      )}

      <div className="pc-compose-foot">
        {modes.positionTags && (
          <div className="pc-stance-pick">
            <label
              className={`pc-favor${position === "favor" ? " pc-checked" : ""}`}
            >
              <input
                type="radio"
                name={`pc-st-${item.id}`}
                checked={position === "favor"}
                onChange={() => onPositionChange("favor")}
              />
              In favor
            </label>
            <label
              className={`pc-neutral${position === null ? " pc-checked" : ""}`}
            >
              <input
                type="radio"
                name={`pc-st-${item.id}`}
                checked={position === null}
                onChange={() => onPositionChange(null)}
              />
              Neutral
            </label>
            <label
              className={`pc-oppose${
                position === "oppose" ? " pc-checked" : ""
              }`}
            >
              <input
                type="radio"
                name={`pc-st-${item.id}`}
                checked={position === "oppose"}
                onChange={() => onPositionChange("oppose")}
              />
              Opposed
            </label>
          </div>
        )}

        <span className="pc-compose-as">
          {allowPrivate && keepPrivate && !asAgency ? (
            <>
              <span className="pc-av-mini pc-av-mini-private">
                <LockIcon />
              </span>
              Posting <strong>privately</strong> to {orgLabel} staff
            </>
          ) : asAgency ? (
            <>
              <span className="pc-av-mini pc-av-mini-staff">
                {initials(orgLabel)}
              </span>
              Posting on behalf of <strong>{orgLabel}</strong>
            </>
          ) : anonymous ? (
            <>
              <span className="pc-av-mini pc-av-mini-anon">
                <PersonIcon />
              </span>
              Posting publicly as <strong>Anonymous</strong>
            </>
          ) : (
            <>
              <span
                className="pc-av-mini"
                style={
                  commenterName
                    ? { background: avatarColor(commenterName) }
                    : undefined
                }
              >
                {commenterName ? initials(commenterName) : "YOU"}
              </span>
              Commenting as <strong>{name}</strong>
            </>
          )}
        </span>

        <button
          type="button"
          className="pc-btn pc-btn-primary"
          onClick={onSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <span className="pc-spin" aria-hidden="true" />
              Submitting...
            </>
          ) : (
            "Submit comment"
          )}
        </button>
      </div>
    </div>
  );
};

export default ItemComposer;
